import * as React from 'react';
import { apiError } from '@/lib/api-error';
import { useQuery } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import { CheckCircle2, Loader2, Mail } from 'lucide-react';
import { toast } from 'sonner';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  confirmEmail,
  fetchEmailVerification,
  resendEmailCode,
} from '@/data/billing';


function retryAfter(error: unknown) {
  if (error instanceof AxiosError) {
    const data = error.response?.data as { retry_after_seconds?: number } | undefined;
    return data?.retry_after_seconds ?? 0;
  }
  return 0;
}

export default function ConfirmEmailStep({ onConfirmed }: { onConfirmed: () => void }) {
  const [code, setCode] = React.useState('');
  const [busy, setBusy] = React.useState(false);
  const [cooldown, setCooldown] = React.useState(0);

  const state = useQuery({
    queryKey: ['email-verification'],
    queryFn: fetchEmailVerification,
    retry: false,
  });

  React.useEffect(() => {
    if (state.data?.verified) onConfirmed();
  }, [state.data?.verified, onConfirmed]);

  React.useEffect(() => {
    if (cooldown <= 0) return undefined;
    const timer = setInterval(() => setCooldown((n) => Math.max(0, n - 1)), 1000);
    return () => clearInterval(timer);
  }, [cooldown]);

  const submit = async () => {
    setBusy(true);
    try {
      await confirmEmail(code.trim());
      toast.success('Email confirmed');
      onConfirmed();
    } catch (error) {
      toast.error(apiError(error, 'That code did not work.'));
    } finally {
      setBusy(false);
    }
  };

  const resend = async () => {
    setBusy(true);
    try {
      await resendEmailCode();
      toast.success('A new code is on its way.');
      setCooldown(300);
    } catch (error) {
      const wait = retryAfter(error);
      if (wait) setCooldown(wait);
      toast.error(apiError(error, 'Could not send another code.'));
    } finally {
      setBusy(false);
    }
  };

  const ready = code.trim().length >= 4 && !busy;

  return (
    <div className="space-y-4" data-testid="confirm-email-step">
      <div className="flex gap-3 rounded-lg border bg-muted/30 p-4">
        <Mail className="mt-0.5 h-5 w-5 shrink-0 text-amber-600" />
        <div className="text-sm">
          <p className="font-medium">We sent a code to {state.data?.email ?? 'your inbox'}.</p>
          <p className="mt-1 text-muted-foreground">
            Enter it below to confirm the address. It is good for 30 minutes.
          </p>
        </div>
      </div>

      <div className="space-y-2">
        <Label htmlFor="verify-code">Confirmation code</Label>
        <Input
          id="verify-code"
          value={code}
          onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
          onKeyDown={(e) => e.key === 'Enter' && ready && submit()}
          placeholder="123456"
          inputMode="numeric"
          autoComplete="one-time-code"
          className="max-w-[200px] font-mono text-lg tracking-[0.4em]"
          data-testid="verify-code-input"
          autoFocus
        />
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <Button onClick={submit} disabled={!ready} data-testid="verify-code-submit">
          {busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
          <CheckCircle2 className="mr-2 h-4 w-4" />
          Confirm
        </Button>
        <Button
          variant="ghost"
          onClick={resend}
          disabled={busy || cooldown > 0}
          data-testid="verify-code-resend"
        >
          {cooldown > 0
            ? `Send another in ${Math.floor(cooldown / 60)}:${String(cooldown % 60).padStart(2, '0')}`
            : 'Send another code'}
        </Button>
      </div>
    </div>
  );
}
